import DataTable from "@/components/application/data-table";
import Layout from "@/components/application/layout";
import PlanLimitAlert from "@/components/application/plan-limit-alert";
import ValidationErrors from "@/components/inertia/validation-errors";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import usePermissions from "@/hooks/use-permissions";
import useTranslations from "@/hooks/use-translations";
import { store_users_path } from "@/lib/routes";
import { AdminPageProps, StoreUser, User } from "@/types";
import { SheetProps } from "@/types/ui";
import { useForm, usePage } from "@inertiajs/react";
import { ColumnDef } from "@tanstack/react-table";
import { CircleAlertIcon, Plus } from "lucide-react";
import { FormEvent, ReactNode, useState } from "react";

function NewUserSheet({ open, onOpenChange }: SheetProps) {
  const { current_store } = usePage<AdminPageProps>().props;
  const { can } = usePermissions();
  const { t } = useTranslations("admin.users.index");

  const newUserForm = useForm({ store_user: { user: "" }, user: null });

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    newUserForm.post(store_users_path(current_store!.id), {
      invalidateCacheTags: ["stores", current_store?.id ?? ""],
      onSuccess() {
        newUserForm.resetAndClearErrors();
        onOpenChange(false);
      },
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="default" disabled={!can("create_store_user")}>
          <Plus />
          {t("new_user")}
        </Button>
      </SheetTrigger>

      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t("add_user")}</SheetTitle>
        </SheetHeader>

        <form
          id="new-user-form"
          method="post"
          className="flex-1 flex flex-col gap-5 px-4 overflow-auto"
          onSubmit={onSubmit}
        >
          <Field data-invalid={!!newUserForm.errors["user"]}>
            <FieldLabel htmlFor="store_user_email">
              {t("email_label")}
            </FieldLabel>
            <Input
              id="store_user_email"
              name="store_user[user]"
              type="email"
              placeholder={t("email_placeholder")}
              className="bg-white"
              aria-invalid={!!newUserForm.errors["user"]}
              value={newUserForm.data.store_user.user}
              onChange={(event) =>
                newUserForm.setData((values) => ({
                  ...values,
                  store_user: {
                    ...values.store_user,
                    user: event.target.value,
                  },
                }))
              }
            />
            {newUserForm.errors["user"] && (
              <ValidationErrors errors={newUserForm.errors["user"]} />
            )}
          </Field>
        </form>
        <SheetFooter>
          <Button
            type="submit"
            form="new-product-form"
            disabled={newUserForm.processing}
          >
            {newUserForm.processing && <Spinner />}
            {t("shared.actions.save")}
          </Button>
          <SheetClose asChild>
            <Button variant="outline" disabled={newUserForm.processing}>
              {t("shared.actions.close")}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function useColumns(): ColumnDef<User>[] {
  const { t } = useTranslations("admin.users.index");

  return [
    {
      accessorKey: "email",
      header: t("table.email"),
    },
  ];
}

function UsersTable({ users }: { users: User[] }) {
  const columns = useColumns();
  return <DataTable<User> columns={columns} data={users} />;
}

function StoreUsersPage({ store_users }: { store_users: StoreUser[] }) {
  const { current_user } = usePage<AdminPageProps>().props;
  const { can } = usePermissions();
  const { t } = useTranslations();
  const [open, changeOpen] = useState<boolean>(false);

  return (
    <div className="flex flex-col gap-6">
      {!can("create_store_user") && (
        <PlanLimitAlert
          title={t("shared.plan_limit_alert.store_users.title")}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-2xl">
          {t("admin.users.index.title")}
        </h2>
        <span />
        <NewUserSheet open={open} onOpenChange={changeOpen} />
      </div>

      <UsersTable
        users={[
          current_user!,
          ...store_users.map((storeUser) => storeUser.user),
        ]}
      />
    </div>
  );
}

StoreUsersPage.layout = (page: ReactNode) => <Layout children={page} />;

export default StoreUsersPage;
